import { datadogRum } from '@datadog/browser-rum'
import { isAbortedRouteChangeError } from 'lib/tracking/helpers/isAbortedRouteChangeError/isAbortedRouteChangeError'
import { isHardNavigateSameUrlError } from 'lib/tracking/helpers/isHardNavigateSameUrlError/isHardNavigateSameUrlError'
import { isAutoCapturedChunkLoadError } from 'lib/tracking/helpers/isAutoCapturedChunkLoadError/isAutoCapturedChunkLoadError'
import { isHubspotFrequencyRulesError } from 'lib/tracking/helpers/isHubspotFrequencyRulesError/isHubspotFrequencyRulesError'
import { isSocureSessionAssociationError } from 'lib/tracking/helpers/isSocureSessionAssociationError/isSocureSessionAssociationError'
import { isSegmentPerformanceMetricsError } from 'lib/tracking/helpers/isSegmentPerformanceMetricsError/isSegmentPerformanceMetricsError'
import { isLaunchDarklyInitTimeoutError } from 'lib/tracking/helpers/isLaunchDarklyInitTimeoutError/isLaunchDarklyInitTimeoutError'
import { isCancelledOperationError } from 'lib/tracking/helpers/isCancelledOperationError/isCancelledOperationError'
import { isThirdPartyThrownError } from 'lib/tracking/helpers/isThirdPartyThrownError/isThirdPartyThrownError'
import { getGraphQLErrors } from 'lib/helpers/getGraphQLErrors/getGraphQLErrors'
import { isBenignNetworkError } from 'lib/helpers/isBenignNetworkError/isBenignNetworkError'
import { isNetworkError } from 'lib/helpers/isNetworkError/isNetworkError'
import { isReactBoundarySilencedError } from 'lib/tracking/helpers/isReactBoundarySilencedError/isReactBoundarySilencedError'
import type { UserTraits } from './types'

type ErrorParams = Record<string, unknown>

interface ErrorNoiseFilter {
  predicate: (params: { message: string; source: string; stack?: string }) => boolean
  onlyWhenBackgrounded: boolean
}

/**
 * Predicates matching classes of non-actionable RUM error noise. `onlyWhenBackgrounded`
 * gates a filter to backgrounded tabs (`view.in_foreground === false`): a fetch-abort
 * message is benign when the user has navigated away, but the same message on a live
 * foreground tab can be a real outage or broken CORS and must still be reported.
 *
 * Predicates receive the reporting `source` alongside the message so a filter can spare the
 * app's own `logError` reports (`source: custom`) while dropping RUM's auto-captured duplicates.
 */
const errorNoiseFilters: ErrorNoiseFilter[] = [
  { predicate: isAbortedRouteChangeError, onlyWhenBackgrounded: false },
  { predicate: isHardNavigateSameUrlError, onlyWhenBackgrounded: false },
  { predicate: isAutoCapturedChunkLoadError, onlyWhenBackgrounded: false },
  { predicate: isSocureSessionAssociationError, onlyWhenBackgrounded: false },
  { predicate: isHubspotFrequencyRulesError, onlyWhenBackgrounded: false },
  { predicate: isSegmentPerformanceMetricsError, onlyWhenBackgrounded: false },
  { predicate: isCancelledOperationError, onlyWhenBackgrounded: false },
  { predicate: isThirdPartyThrownError, onlyWhenBackgrounded: false },
  { predicate: isBenignNetworkError, onlyWhenBackgrounded: true },
  { predicate: isLaunchDarklyInitTimeoutError, onlyWhenBackgrounded: true },
]

export const initDatadog = () => {
  const applicationId = process.env.DATADOG_APPLICATION_ID
  const clientToken = process.env.DATADOG_CLIENT_TOKEN

  if (applicationId && clientToken) {
    datadogRum.init({
      applicationId,
      clientToken,
      env: process.env.VERCEL_ENV || 'development',
      version: process.env.SHORT_SHA,
      site: 'datadoghq.com',
      service: 'settle-web',
      traceSampleRate: 100,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      allowUntrustedEvents: true,
      allowedTracingUrls: [process.env.BACKEND_APP_URL],
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: (event, context) => {
        if (event.type !== 'error') {
          return true
        }

        const originalError = 'error' in context ? context.error : undefined

        // Drop RUM's duplicate of an error a React boundary already handled without reporting.
        if (
          isReactBoundarySilencedError({
            source: event.error.source,
            handlingStack: event.error.handling_stack,
            error: originalError,
          })
        ) {
          return false
        }

        // A request that never reached the server is not an application fault. Matching on Apollo's
        // link-chain registry rather than the message keeps this working across browsers, which word
        // the failure differently. The rate stays visible as the error link's
        // `graphql_transport_failure` action.
        if (isNetworkError(originalError)) {
          return false
        }

        const { message, source, stack } = event.error
        const isBackgrounded = event.view?.in_foreground === false

        const isNoise = errorNoiseFilters.some(
          ({ predicate, onlyWhenBackgrounded }) =>
            (!onlyWhenBackgrounded || isBackgrounded) && predicate({ message, source, stack }),
        )

        return !isNoise
      },
    })

    datadogRum.startSessionReplayRecording()
  }
}

export const addAction = (name: string, context?: Record<string, unknown>) => {
  datadogRum.addAction(name, context)
}

export const updateUser = (userId?: string | null, traits: UserTraits = {}) => {
  datadogRum.setUser({
    id: userId ?? undefined,
    userId,
    ...traits,
  })
}

/**
 * Can be used to log errors on client and server side
 */
export const logError = (err: unknown, params?: ErrorParams): void => {
  datadogRum.addError(err, params)
}

/**
 * Reports each GraphQL error the server returned as its own RUM error.
 *
 * Reports **nothing** for an error carrying no GraphQL errors — a network failure or a plain
 * `Error` is silently ignored. Use {@link logError} for those; this is not a general error sink.
 */
export const logGraphQLErrors = (err: unknown, params?: ErrorParams): void => {
  getGraphQLErrors(err).forEach((error) => logError(error, params))
}
